// What the records forms (and the console's Retention tab) check before they
// send — the server's rules (app/modules/records/schemas.py), mirrored so the
// person sees a problem beside its field instead of after a round trip. The
// server stays the authority: its answers are placed with fieldErrors().
//
// Each `*Problems` returns { field: message } (empty = fine). Text is judged
// trimmed, as it is sent.

import i18n from '@/i18n';

export const LIMITS = {
  code: 32,
  sectionCode: 16,
  caseTitle: 2000,
  unit: 255,
  note: 4000,
  planTitle: 500,
  title: 1000,
  journalName: 200,
  pattern: 64,
  correspondent: 2000,
  subject: 4000,
  signedBy: 255,
  theirNumber: 80,
  reasonMin: 3,
  reason: 2000,
  actConfirm: 32,
  yearMin: 1990,
  yearMax: 2200,
  periodYearsMax: 200,
  positionMax: 100000,
  dateMin: '1900-01-01',
  dateMax: '2500-12-31',
  regDateMin: '1990-01-01',
};

// A section's code, a class's article, a case's index (CODE_PATTERN).
const CODE = /^[\p{L}\p{N}][\p{L}\p{N} .,_/()№-]*$/u;
export const isCode = (text) => CODE.test(text || '');

const t = (key, params) => i18n.t(`errors:fields.${key}`, params);
const text = (value) => (typeof value === 'string' ? value.trim() : '');

// DD.MM.YYYY for messages, from YYYY-MM-DD.
const day = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-');
  return d ? `${d}.${m}.${y}` : String(iso || '');
};

// YYYY-MM-DD one day later, without time zones.
export function nextDay(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().slice(0, 10);
}

function textRule(out, field, value, { required = false, max, code = false } = {}) {
  const v = text(value);
  if (!v) {
    if (required) out[field] = t('required');
    return;
  }
  if (max && v.length > max) out[field] = t('tooLong', { max });
  else if (code && !isCode(v)) out[field] = t('code');
}

function intRule(out, field, value, { min, max, required = true }) {
  if (value === '' || value === null || value === undefined) {
    if (required) out[field] = t('required');
    return;
  }
  const n = Number(value);
  if (!Number.isInteger(n)) out[field] = t('integer');
  else if (n < min || n > max) out[field] = t('range', { min, max });
}

export function reasonProblem(reason) {
  const v = text(reason);
  if (!v) return t('required');
  if (v.length < LIMITS.reasonMin) return t('tooShort', { min: LIMITS.reasonMin });
  if (v.length > LIMITS.reason) return t('tooLong', { max: LIMITS.reason });
  return null;
}

export function caseProblems(form) {
  const out = {};
  textRule(out, 'case_index', form.case_index, { required: true, max: LIMITS.code, code: true });
  textRule(out, 'title', form.title, { required: true, max: LIMITS.caseTitle });
  textRule(out, 'unit', form.unit, { max: LIMITS.unit });
  textRule(out, 'note', form.note, { max: LIMITS.note });
  if (!form.retention_class_id) out.retention_class_id = t('required');
  return out;
}

export function planProblems({ year, title, note }, takenYears = []) {
  const out = {};
  if (year !== undefined) {
    intRule(out, 'year', year, { min: LIMITS.yearMin, max: LIMITS.yearMax });
    if (!out.year && takenYears.includes(Number(year))) out.year = i18n.t('errors:file_plan_exists', { year: Number(year) });
  }
  textRule(out, 'title', title, { max: LIMITS.planTitle });
  textRule(out, 'note', note, { max: LIMITS.note });
  return out;
}

// `today`: YYYY-MM-DD in the records time zone.
export function registrationProblems(form, today) {
  const out = {};
  const reg = form.reg_date;
  if (!reg) out.reg_date = t('required');
  else if (reg > today) out.reg_date = t('future');
  else if (reg < LIMITS.regDateMin) out.reg_date = t('dateMin', { date: day(LIMITS.regDateMin) });
  if (form.their_date) {
    if (reg && form.their_date > reg) out.their_date = i18n.t('errors:their_date_after_registration', { date: day(reg) });
    else if (form.their_date < LIMITS.dateMin) out.their_date = t('dateMin', { date: day(LIMITS.dateMin) });
  }
  if (form.due_date) {
    if (reg && form.due_date < reg) out.due_date = i18n.t('errors:due_date_before_registration', { date: day(reg) });
    else if (form.due_date > LIMITS.dateMax) out.due_date = t('dateMax', { date: day(LIMITS.dateMax) });
  }
  textRule(out, 'correspondent', form.correspondent, { max: LIMITS.correspondent });
  textRule(out, 'subject', form.subject, { max: LIMITS.subject });
  textRule(out, 'signed_by', form.signed_by, { max: LIMITS.signedBy });
  textRule(out, 'their_number', form.their_number, { max: LIMITS.theirNumber });
  textRule(out, 'note', form.note, { max: LIMITS.note });
  return out;
}

// A later keep-until date: after today and after the case's current date.
export function extendProblems({ until, reason }, { today, current = null }) {
  const out = {};
  const floor = current && current > today ? current : today;
  if (!until) out.until = t('required');
  else if (until <= floor) out.until = t('dateMin', { date: day(nextDay(floor)) });
  else if (until > LIMITS.dateMax) out.until = t('dateMax', { date: day(LIMITS.dateMax) });
  const why = reasonProblem(reason);
  if (why) out.reason = why;
  return out;
}

export function titleProblems(title, { required = true } = {}) {
  const values = Object.entries(title || {}).map(([lang, v]) => [lang, text(v)]);
  const out = {};
  values.forEach(([lang, v]) => {
    if (v.length > LIMITS.title) out[`title.${lang}`] = t('tooLong', { max: LIMITS.title });
  });
  if (required && !values.some(([, v]) => v)) out.title = i18n.t('errors:fields.types.title_required');
  return out;
}

export function classProblems(form) {
  const out = { ...titleProblems(form.title) };
  textRule(out, 'article', form.article, { required: true, max: LIMITS.code, code: true });
  if (form.period_kind === 'years') {
    intRule(out, 'period_years', form.period_years, { min: 1, max: LIMITS.periodYearsMax });
  }
  textRule(out, 'note', form.note, { max: LIMITS.note });
  return out;
}

export function sectionProblems(form) {
  const out = { ...titleProblems(form.title) };
  textRule(out, 'code', form.code, { required: true, max: LIMITS.sectionCode, code: true });
  intRule(out, 'position', form.position, { min: 0, max: LIMITS.positionMax, required: false });
  return out;
}

export const hasProblems = (problems) => Object.keys(problems).length > 0;
